import { CheckCircle, User } from '@phosphor-icons/react';
import {
	capitalizeText,
	formatCurrency,
} from '../../../shared/utils/general.util';
import { AnnualPreviewData } from '../../../@types/AnnualPreview.type';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import BannerLinkFiscalAccount from '../../../assets/banner-link-fiscal-annual.svg';
import { DeclarationsOverview } from '../../../components/organisms/DeclarationsOverview/DeclarationsOverview';
import AnnualPreviewDeclarationResultConfig from '../components/AnnualPreviewDeclarationResultConfig';
import { Dispatch } from 'react';
import { SetStateAction } from 'react';
import { SupportIssue } from '../../../@types/Profile.type';
import useAnnualLogic from '../../../hooks/useAnnualLogic';

const InfoItem = ({
	label,
	value,
}: {
	label: string;
	value: number | string | null;
}) => {
	return (
		<div className='flex justify-between w-full'>
			<p className='font-light'>{label}:</p>
			<p className='font-medium'>
				{typeof value === 'number' ? formatCurrency(value) : value}
			</p>
		</div>
	);
};

const AlertBanner = ({
	title,
	description,
}: {
	title: string;
	description: string;
}) => {
	return (
		<div className='bg-gradient-to-tr from-h-primary-90/80 to-h-primary-90/20 rounded-lg border border-h-primary-100 p-4 shadow-sm text-h-dark flex items-center gap-3 w-full'>
			<CheckCircle
				size={24}
				className='text-h-primary min-w-6'
				weight='duotone'
			/>
			<div className='text-sm md:text-sm leading-4.5 flex flex-col'>
				<p className='font-medium'>{title}</p>
				<p className='font-light'>{description}.</p>
			</div>
		</div>
	);
};

const AnnualPreviewStep1 = ({
	data,
	userIsLinked,
	userHasPurchasedAnnualSubscription,
	setShowModal,
	supportIssues,
}: {
	data: AnnualPreviewData | null;
	userIsLinked: boolean;
	userHasPurchasedAnnualSubscription: boolean;
	setShowModal: Dispatch<
		SetStateAction<{
			positiveBalance: boolean;
			unlinked: boolean;
			paymentInstallments: boolean;
			annualPreferences: boolean;
		}>
	>;
	supportIssues: SupportIssue[];
}) => {
	const {
		haveMultipleRegimes,
		haveMoreThan400000Incomes,
		hasMultipleIssuersInRegime,
	} = useAnnualLogic();
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const taxPayer = useSelector((state: RootState) => state.taxProfile.taxPayer);
	const fullName = `${userProfile?.first_name} ${userProfile?.father_surname}`;
	const noViewAllIssues = [509171, 477823];

	const renderAlertBanner = () => {
		const alerts = {
			rule400k: {
				title: `Estás obligado a presentar tu declaración anual 2024`,
				description: `Tus ingresos anuales en el 2024 superan los $400,000 MXN, por lo tanto estás obligado a presentar tu declaración anual`,
			},
			ruleMultipleRegimes: {
				title: `Estás obligado a presentar tu declaración anual 2024`,
				description: `Durante el 2024 generaste ingresos por dos o más regímenes fiscales, por lo tanto estás obligado a presentar tu declaración anual`,
			},
			haveMoreThanOneBoss: {
				title: `Estás obligado a presentar tu declaración anual 2024`,
				description: `En el 2024 obtuviste ingresos por parte de dos o más empresas, por lo tanto estás obligado a presentar tu declaración anual`,
			},
		};

		if (haveMoreThan400000Incomes) {
			return (
				<AlertBanner
					title={alerts.rule400k.title}
					description={alerts.rule400k.description}
				/>
			);
		}
		if (haveMultipleRegimes) {
			return (
				<AlertBanner
					title={alerts.ruleMultipleRegimes.title}
					description={alerts.ruleMultipleRegimes.description}
				/>
			);
		}
		if (hasMultipleIssuersInRegime) {
			return (
				<AlertBanner
					title={alerts.haveMoreThanOneBoss.title}
					description={alerts.haveMoreThanOneBoss.description}
				/>
			);
		}
	};

	return (
		<div className='flex flex-col gap-6'>
			<div className='bg-white rounded-xl p-4 flex flex-col items-center gap-6 relative'>
				<div className='flex flex-col items-center gap-1 z-[1]'>
					<User
						weight='duotone'
						size={32}
						className='text-h-primary'
					/>
					<p className='text-base md:text-lg font-medium'>
						{capitalizeText(fullName)}
					</p>
					<p className='text-sm md:text-base text-h-medium font-light'>
						RFC: {taxPayer?.code || '-'}
					</p>
				</div>
				{!userHasPurchasedAnnualSubscription && renderAlertBanner()}
				{userHasPurchasedAnnualSubscription && (
					<div className='font-medium flex items-center gap-2 leading-tight text-sm  rounded-lg p-2 bg-h-success-50/10 w-full justify-center'>
						<CheckCircle
							weight='duotone'
							className='text-h-success-50'
							size={24}
						/>
						<p>
							¡Felicidades! Tu declaración anual 2024 ya está pagada y cubierta
							con heru
						</p>
					</div>
				)}
				<div className='flex flex-col gap-3 items-start justify-start w-full'>
					<p className='font-medium'>Información general</p>
					{data
						? data?.general?.map((item) => (
								<InfoItem
									key={item.label}
									label={item.label}
									value={item.value}
								/>
							))
						: [
								{ label: 'Ingresos identificados', value: '$ -' },
								{ label: 'Deducciones personales', value: '$ -' },
							].map((item) => (
								<InfoItem
									key={item.label}
									label={item.label}
									value={item.value}
								/>
							))}
					{userHasPurchasedAnnualSubscription && (
						<a
							href='https://noticias.heru.app/en/articles/5360705'
							target='_blank'
							className='text-sm leading-tight tracking-tight bg-h-primary-95 text-h-primary-20 w-full rounded-lg p-2 '
						>
							{data?.result?.total_deductions === 0 ? (
								<>
									<b>
										*Estamos revisando dentro de tus gastos por posibles
										deducciones{' '}
									</b>
									personales, no te preocupes, aplicaremos todas las deducciones
									personales que encontremos en tus CFDIs.
								</>
							) : (
								<>
									<b>
										*Estamos revisando dentro de tus gastos por posibles
										deducciones{' '}
									</b>
									personales, no te preocupes, al presentar tu declaración
									aplicaremos todas las deducciones personales que encontremos
									en tus CFDIs.
								</>
							)}
						</a>
					)}
				</div>
				{!userIsLinked && (
					<img
						src={BannerLinkFiscalAccount}
						alt='Banner Link Fiscal Account'
						className='w-full h-full object-cover rounded-xl border-4 border-white shadow'
					/>
				)}
			</div>

			{userHasPurchasedAnnualSubscription && (
				<>
					{!noViewAllIssues.includes(userProfile?.id!) && (
						<DeclarationsOverview page='annual' />
					)}

					<AnnualPreviewDeclarationResultConfig
						setShowModal={setShowModal}
						supportIssues={supportIssues}
					/>
				</>
			)}
		</div>
	);
};

export default AnnualPreviewStep1;
